// src/hooks/useDashboardData.js
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  fetchSummary,
  fetchShipmentsOverview,
  fetchShipmentsStatus,
  fetchShipmentsZoneWise,
  fetchCourierSplit,
  fetchDeliveryPerformance,
  fetchRevenue,
  fetchNdr,
  fetchCod,
  fetchAttemptAnalytics,
} from "../services/dashboardService";

/**
 * Custom hook to manage all dashboard data fetching, caching,
 * loading states, and automatic refresh.
 */
export default function useDashboardData() {
  // ── Filter state ──────────────────────────────────────────
  const [period, setPeriod] = useState("TODAY");
  const [fromDateTime, setFromDateTime] = useState(null);
  const [toDateTime, setToDateTime] = useState(null);
  const [carrierPartnerId, setCarrierPartnerId] = useState(null);

  // ── Data state ────────────────────────────────────────────
  const [summary, setSummary] = useState(null);
  const [shipmentsOverview, setShipmentsOverview] = useState(null);
  const [shipmentsStatus, setShipmentsStatus] = useState(null);
  const [shipmentsZoneWise, setShipmentsZoneWise] = useState(null);
  const [courierSplit, setCourierSplit] = useState(null);
  const [deliveryPerformance, setDeliveryPerformance] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [ndr, setNdr] = useState(null);
  const [cod, setCod] = useState(null);
  const [attemptAnalytics, setAttemptAnalytics] = useState(null);

  // ── Loading / error states ────────────────────────────────
  const [loading, setLoading] = useState({
    summary: false,
    shipmentsOverview: false,
    shipmentsStatus: false,
    shipmentsZoneWise: false,
    courierSplit: false,
    deliveryPerformance: false,
    revenue: false,
    ndr: false,
    cod: false,
    attemptAnalytics: false,
  });

  const [errors, setErrors] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  const refreshTimerRef = useRef(null);

  // ── Build request options object ──────────────────────────
  const getOpts = useCallback(() => ({
    period,
    fromDateTime,
    toDateTime,
    carrierPartnerId,
  }), [period, fromDateTime, toDateTime, carrierPartnerId]);

  // ── Generic fetcher with per-section loading ──────────────
  const fetchSection = useCallback(async (key, fetcher, setter) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    setErrors((prev) => ({ ...prev, [key]: null }));
    try {
      const data = await fetcher(getOpts());
      setter(data);
    } catch (err) {
      const message = err?.message || "Failed to load data";
      setErrors((prev) => ({ ...prev, [key]: message }));
      console.error(`Dashboard [${key}] error:`, err);
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, [getOpts]);

  // ── Fetch all sections in parallel ────────────────────────
  const fetchAll = useCallback(async () => {
    const sections = [
      { key: "summary", fetcher: fetchSummary, setter: setSummary },
      { key: "shipmentsOverview", fetcher: fetchShipmentsOverview, setter: setShipmentsOverview },
      { key: "shipmentsStatus", fetcher: fetchShipmentsStatus, setter: setShipmentsStatus },
      { key: "shipmentsZoneWise", fetcher: fetchShipmentsZoneWise, setter: setShipmentsZoneWise },
      { key: "courierSplit", fetcher: fetchCourierSplit, setter: setCourierSplit },
      { key: "deliveryPerformance", fetcher: fetchDeliveryPerformance, setter: setDeliveryPerformance },
      { key: "revenue", fetcher: fetchRevenue, setter: setRevenue },
      { key: "ndr", fetcher: fetchNdr, setter: setNdr },
      { key: "cod", fetcher: fetchCod, setter: setCod },
      { key: "attemptAnalytics", fetcher: fetchAttemptAnalytics, setter: setAttemptAnalytics },
    ];

    await Promise.allSettled(
      sections.map(({ key, fetcher, setter }) => fetchSection(key, fetcher, setter))
    );

    setLastRefresh(new Date());
  }, [fetchSection]);

  // ── Manual refresh ────────────────────────────────────────
  const refresh = useCallback(() => {
    fetchAll();
    toast.info("Dashboard data refreshing...", { autoClose: 1500 });
  }, [fetchAll]);

  // ── Auto-refresh every 5 minutes ─────────────────────────
  useEffect(() => {
    fetchAll();

    refreshTimerRef.current = setInterval(() => {
      fetchAll();
    }, 5 * 60 * 1000);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [fetchAll]);

  // ── Derived: is anything still loading? ───────────────────
  const isAnyLoading = Object.values(loading).some(Boolean);
  const isInitialLoad = isAnyLoading && !summary;

  return {
    // Filter controls
    period,
    setPeriod,
    fromDateTime,
    setFromDateTime,
    toDateTime,
    setToDateTime,
    carrierPartnerId,
    setCarrierPartnerId,

    // Data
    summary,
    shipmentsOverview,
    shipmentsStatus,
    shipmentsZoneWise,
    courierSplit,
    deliveryPerformance,
    revenue,
    ndr,
    cod,
    attemptAnalytics,

    // States
    loading,
    errors,
    isAnyLoading,
    isInitialLoad,
    lastRefresh,

    // Actions
    refresh,
    fetchAll,
  };
}
